<template>
  <div class="sell-order-details-container">
    <div class="market-box">
      <div class="back-box" @click="$router.push({ path: '/user', query: { type: 7 } })">
        <img
          src="@/assets/img/images/icon/back-border.png"
          alt="back-border.png"
        />
        <p>{{ $t("sellOrderDetails.back_to_order_list") }}</p>
      </div>
      <div class="all-box">
        <div class="right-box">
          <div class="details-content">
            <div class="product-card">
              <div
                v-if="orderDetail.status === 'pending_pay'"
                class="countdown-card"
              >
                <span class="countdown-label">{{
                  $t("sellOrderDetails.payment_countdown")
                }}</span>
                <span class="countdown-value">{{ countdownText }}</span>
              </div>

              <div class="product-header">
                <div class="product-info">
                  <img
                    class="product-icon"
                    :src="orderDetail.coinIcon || ''"
                    @error="onIconError"
                  />
                  <div class="product-name-wrap">
                    <span class="product-name">{{
                      orderDetail.projectName || ""
                    }}</span>
                    <span class="product-tag">
                      {{
                        orderDetail.saleType === 1
                          ? $t("mall.pre_sale")
                          : $t("mall.open_sale")
                      }}
                    </span>
                  </div>
                </div>
                <span :class="['status-tag', orderDetail.status]">{{
                  getStatusText(orderDetail.status)
                }}</span>
              </div>
            </div>

            <div class="detail-card">
              <div class="detail-row">
                <span class="detail-label">{{
                  $t("sellOrderDetails.subscription_shares")
                }}</span>
                <span class="detail-value"
                  >{{ orderDetail.shares || 0 }}
                  {{ $t("mall.shares_unit") }}</span
                >
              </div>
              <div class="detail-row">
                <span class="detail-label">{{
                  $t("sellOrderDetails.subscription_amount")
                }}</span>
                <span class="detail-value"
                  >{{ (orderDetail.distributeAmount || 0).toLocaleString() }}
                  {{ $t("mall.issue_unit") }}</span
                >
              </div>
              <div class="detail-row">
                <span class="detail-label">{{
                  $t("sellOrderDetails.issue_price")
                }}</span>
                <span class="detail-value"
                  >{{ orderDetail.priceUsd || 0 }} USD</span
                >
              </div>
              <div class="detail-row">
                <span class="detail-label">{{
                  $t("sellOrderDetails.total_payment")
                }}</span>
                <span class="detail-value"
                  >{{
                    orderDetail.payAssetType === "HKD"
                      ? orderDetail.payAmountHkd
                      : orderDetail.payAmountUsd || 0
                  }}
                  {{ orderDetail.payAssetType }}</span
                >
              </div>
            </div>

            <div class="separator-line"></div>

            <div class="trade-card">
              <div class="detail-row">
                <span class="detail-label">{{
                  $t("sellOrderDetails.order_time")
                }}</span>
                <span class="detail-value">{{
                  formatTimestamp(orderDetail.createTime) || "--"
                }}</span>
              </div>
              <div class="detail-row">
                <span class="detail-label">{{
                  $t("sellOrderDetails.distribution_time")
                }}</span>
                <span class="detail-value">{{
                  formatTimestamp(orderDetail.distributeTime) || "--"
                }}</span>
              </div>
              <div class="detail-row">
                <span class="detail-label">{{
                  $t("sellOrderDetails.transaction_time")
                }}</span>
                <span class="detail-value transaction-info">{{
                  orderDetail.planTradeTime || "--"
                }}</span>
              </div>
              <div class="detail-row order-no-row">
                <span class="detail-label">{{
                  $t("sellOrderDetails.order_no")
                }}</span>
                <div class="order-no-wrap">
                  <span class="detail-value order-no">{{
                    orderDetail.orderNo || "--"
                  }}</span>
                  <span class="copy-btn" @click="copyOrderNo">{{
                    $t("sellOrderDetails.copy")
                  }}</span>
                </div>
              </div>
            </div>

            <div
              v-if="
                orderDetail.status === 'completed' ||
                orderDetail.status === 'pending_distribute'
              "
              class="tip-card"
              @click="goToAsset"
            >
              <span class="tip-text">{{
                $t("sellOrderDetails.asset_tip")
              }}</span>
              <i class="el-icon-arrow-right" />
            </div>

            <div
              v-if="orderDetail.status === 'pending_pay'"
              class="bottom-actions"
            >
              <el-button
                type="default"
                size="large"
                class="action-btn cancel-btn"
                @click="cancelOrder"
              >
                {{ $t("sellOrderDetails.cancel_order") }}
              </el-button>
              <el-button
                type="primary"
                size="large"
                class="action-btn pay-btn"
                @click="goPay"
              >
                {{ $t("sellOrderDetails.go_pay") }}
              </el-button>
            </div>
          </div>
        </div>
      </div>
    </div>

    <VFooter />
    <QuestionPopup />

    <el-dialog :visible.sync="showPayModal" width="500px" :show-close="false">
      <div class="pay-modal-content">
        <div class="modal-header">
          <span class="modal-title">{{ $t("sellOrder.pay") }}</span>
          <span class="modal-close" @click="showPayModal = false">×</span>
        </div>

        <div class="pay-amount-section">
          <span class="pay-label">{{ $t("sellOrder.pay_amount") }}</span>
          <span class="pay-amount">${{ totalPayment.toLocaleString() }}</span>
        </div>

        <div class="pay-method-section">
          <span class="section-title">{{ $t("sellOrder.pay_method") }}</span>
          <div
            v-for="method in payMethods"
            :key="method.value"
            :class="[
              'pay-method-item',
              { active: selectedPayMethod === method.value },
            ]"
            @click="selectedPayMethod = method.value"
          >
            <div class="method-radio">
              <div
                v-if="selectedPayMethod === method.value"
                class="radio-inner"
              ></div>
            </div>
            <span class="method-name">{{ method.label }}</span>
            <span class="method-balance"
              >{{ $t("sellOrder.balance") }}: {{ method.balance }}
              {{ method.value }}</span
            >
          </div>
        </div>

        <div class="password-section">
          <span class="section-title">{{
            $t("sellOrder.trade_password")
          }}</span>
          <el-input
            type="password"
            v-model="tradePassword"
            :placeholder="$t('sellOrder.password_placeholder')"
            class="password-input"
          />
        </div>

        <div class="modal-footer">
          <div class="submit-btn" @click="confirmPayment">
            {{ $t("sellOrder.confirm_pay") }}
          </div>
        </div>
      </div>
    </el-dialog>

    <el-dialog
      :visible.sync="showSuccessModal"
      width="400px"
      :show-close="false"
    >
      <div class="success-modal-content">
        <div class="success-icon">OK</div>
        <span class="success-title">{{ $t("sellOrder.pay_success") }}</span>
        <div class="success-btn" @click="handleSuccessClose">
          {{ $t("sellOrder.confirm") }}
        </div>
      </div>
    </el-dialog>
  </div>
</template>

<script>
import { mapState } from "vuex";
import VFooter from "@/components/footer/VFooter";
import QuestionPopup from "@/components/QuestionPopup/index";
export default {
  components: {
    VFooter,
    QuestionPopup,
  },
  data() {
    return {
      orderDetail: {
        status: "",
        coinIcon: "",
        projectName: "",
        saleType: 0,
        shares: 0,
        distributeAmount: 0,
        priceUsd: 0,
        payAmountUsd: 0,
        payAmountHkd: 0,
        payAssetType: "USD",
        createTime: "",
        distributeTime: "",
        planTradeTime: "",
        orderNo: "",
        expireTime: "",
      },
      orderNo: "",
      loading: false,
      countdown: 0,
      countdownText: "",
      timer: null,
      showPayModal: false,
      showSuccessModal: false,
      totalPayment: 0,
      selectedPayMethod: "USD",
      tradePassword: "",
      payMethods: [
        { label: "USD", value: "USD", balance: 100000 },
        { label: "HKD", value: "HKD", balance: 500000 },
      ],
    };
  },
  computed: {
    ...mapState({
      user: "user",
      errorCode: "errorCode",
    }),
  },
  mounted() {
    if (this.$route.query.orderNo) {
      this.orderNo = this.$route.query.orderNo;
      this.fetchOrderDetail();
    }
  },
  beforeDestroy() {
    if (this.timer) {
      clearInterval(this.timer);
    }
  },
  methods: {
    onIconError(e) {
      e.target.src = null;
    },
    goToAsset() {
      this.$router.push("/wallet");
    },
    async fetchOrderDetail() {
      if (!this.orderNo) {
        return;
      }
      this.loading = true;
      try {
        const res = await this.$axios.post("/mall/order/detail", {
          orderNo: this.orderNo,
        });
        if (res.data.code === this.errorCode.ok && res.data.data) {
          this.orderDetail = res.data.data;
          this.totalPayment = Number(
            this.orderDetail.payAssetType === "HKD"
              ? this.orderDetail.payAmountHkd || 0
              : this.orderDetail.payAmountUsd || 0,
          );
          if (this.orderDetail.status === "pending_pay") {
            let expireTimeMs = this.orderDetail.expireTime;
            let isValid = true;
            if (typeof expireTimeMs === "string") {
              const parsedDate = new Date(expireTimeMs);
              if (isNaN(parsedDate.getTime())) {
                isValid = false;
              } else {
                expireTimeMs = parsedDate.getTime();
              }
            } else if (typeof expireTimeMs === "number") {
              if (expireTimeMs < 1000) {
                isValid = false;
              } else if (expireTimeMs < 10000000000) {
                expireTimeMs = expireTimeMs * 1000;
              }
            } else {
              isValid = false;
            }
            if (!isValid) {
              expireTimeMs = Date.now();
            }
            this.countdown = Math.max(
              0,
              Math.floor((expireTimeMs - Date.now()) / 1000),
            );
            this.startCountdown();
          }
        }
      } catch (error) {
        this.$message.error(this.$t("common.error"));
      } finally {
        this.loading = false;
      }
    },
    getStatusText(status) {
      const statusMap = {
        completed: this.$t("sellOrder.status_completed"),
        pending_distribute: this.$t("sellOrder.status_pending_distribution"),
        pending_pay: this.$t("sellOrder.status_pending_payment"),
        canceled: this.$t("sellOrder.status_cancelled"),
      };
      return statusMap[status] || "";
    },
    formatTimestamp(timestamp) {
      if (!timestamp) return "";
      const date = new Date(timestamp);
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, "0");
      const day = String(date.getDate()).padStart(2, "0");
      const hours = String(date.getHours()).padStart(2, "0");
      const minutes = String(date.getMinutes()).padStart(2, "0");
      const seconds = String(date.getSeconds()).padStart(2, "0");
      return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
    },
    startCountdown() {
      this.updateCountdownText();
      this.timer = setInterval(() => {
        if (this.countdown > 0) {
          this.countdown--;
          this.updateCountdownText();
        } else {
          clearInterval(this.timer);
        }
      }, 1000);
    },
    updateCountdownText() {
      const hours = Math.floor(this.countdown / 3600);
      const minutes = Math.floor((this.countdown % 3600) / 60);
      const seconds = this.countdown % 60;
      this.countdownText = `${String(hours).padStart(2, "0")}:${String(
        minutes,
      ).padStart(2, "0")}:${String(seconds).padStart(2, "0")}`;
    },
    copyOrderNo() {
      navigator.clipboard
        .writeText(this.orderDetail.orderNo)
        .then(() => {
          this.$message.success(this.$t("tipsVal.copySucess"));
        })
        .catch(() => {
          this.$message.warning(this.$t("tipsVal.copyerror"));
        });
    },
    cancelOrder() {
      this.$confirm(
        this.$t("sellOrder.cancel_confirm"),
        this.$t("sellOrder.cancel_tip"),
        {
          confirmButtonText: this.$t("confirm"),
          cancelButtonText: this.$t("cancel"),
          type: "warning",
        },
      )
        .then(async () => {
          try {
            const cancelRes = await this.$axios.post("/mall/order/cancel", {
              orderNo: this.orderNo,
            });
            if (cancelRes.data.code === this.errorCode.ok) {
              this.$message.success(this.$t("sellOrder.cancel_success"));
              this.fetchOrderDetail();
            } else {
              this.$message.error(
                cancelRes.data.msg || this.$t("sellOrder.cancel_fail"),
              );
            }
          } catch (error) {
            this.$message.error(this.$t("sellOrder.cancel_fail"));
          }
        })
        .catch(() => {});
    },
    goPay() {
      this.selectedPayMethod = "USD";
      this.tradePassword = "";
      this.showPayModal = true;
    },
    async confirmPayment() {
      if (!this.tradePassword) {
        this.$message.warning(this.$t("sellOrder.password_required"));
        return;
      }
      try {
        const payRes = await this.$axios.post("/mall/order/pay", {
          orderNo: this.orderNo,
          payMethod: this.selectedPayMethod,
          tradePassword: this.tradePassword,
        });
        if (payRes.data.code === this.errorCode.ok) {
          this.showPayModal = false;
          this.showSuccessModal = true;
          this.tradePassword = "";
          this.fetchOrderDetail();
        } else {
          this.$message.error(payRes.data.msg || this.$t("sellOrder.pay_fail"));
        }
      } catch (error) {
        this.$message.error(this.$t("sellOrder.pay_fail"));
      }
    },
    handleSuccessClose() {
      this.showSuccessModal = false;
    },
  },
};
</script>

<style lang="less" scoped>
.sell-order-details-container {
  .market-box {
    position: relative;
    background: #f5f6f7;
    padding: 60px 260px 120px 260px;
    .back-box {
      gap: 28px;
      display: flex;
      cursor: pointer;
      align-items: center;
      padding-bottom: 40px;

      img {
        width: 44px;
        height: 44px;
      }

      p {
        font-family: PingFang SC, PingFang SC;
        font-weight: 800;
        font-size: 32px;
        color: #333333;
      }
    }
    .all-box {
      gap: 28px;
      display: flex;
      .left-box {
        padding: 40px;
        width: 320px;
        gap: 16px;
        height: fit-content;
        display: flex;
        flex-direction: column;
        background: #ffffff;
        border-radius: 16px;
        .left-item-box {
          gap: 20px;
          display: flex;
          cursor: pointer;
          padding: 20px 24px;
          align-items: center;
          font-family: PingFang SC, PingFang SC;
          font-weight: 400;
          font-size: 18px;
          color: #666666;
          border-radius: 12px;
          transition: all 0.3s;
        }
        .sel {
          font-weight: 600;
          font-size: 18px;
          color: #4063ff;
          background: #e1eaff;
        }
      }
      .right-box {
        flex: 1;
        display: flex;
        flex-direction: column;
        gap: 24px;
      }
    }
  }
}

.icon-de {
  width: 44px;
  height: 44px;
}

.details-content {
  background: #ffffff;
  border-radius: 16px;
  padding: 24px;
}

.product-card {
  margin-bottom: 24px;

  .countdown-card {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 16px 20px;
    background: #fff7e6;
    border-radius: 8px;
    margin-bottom: 20px;

    .countdown-label {
      font-size: 14px;
      color: #fa8c16;
    }

    .countdown-value {
      font-size: 18px;
      font-weight: 600;
      color: #fa8c16;
      font-family: monospace;
    }
  }

  .product-header {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;

    .product-info {
      display: flex;
      align-items: center;
      gap: 16px;

      .product-icon {
        width: 64px;
        height: 64px;
        border-radius: 12px;
        background: #f0f0f0;
      }

      .product-name-wrap {
        .product-name {
          display: block;
          font-size: 20px;
          font-weight: 600;
          color: #333333;
          margin-bottom: 8px;
        }

        .product-tag {
          font-size: 12px;
          color: #999999;
          background: #f5f5f5;
          padding: 4px 10px;
          border-radius: 4px;
        }
      }
    }

    .status-tag {
      font-size: 14px;
      font-weight: 600;
      padding: 6px 16px;
      border-radius: 20px;

      &.completed {
        color: #52c41a;
        background: #f6ffed;
      }

      &.pending_distribute {
        color: #faad14;
        background: #fff7e6;
      }

      &.pending_pay {
        color: #ff4d4f;
        background: #fff1f0;
      }

      &.canceled {
        color: #999999;
        background: #f5f5f5;
      }
    }
  }
}

.detail-card {
  background: #f9fafb;
  border-radius: 12px;
  padding: 20px;
  margin-bottom: 20px;

  .detail-row {
    display: flex;
    justify-content: space-between;
    padding: 12px 0;
    border-bottom: 1px solid #f0f0f0;

    &:last-child {
      border-bottom: none;
    }

    .detail-label {
      font-size: 14px;
      color: #999999;
    }

    .detail-value {
      font-size: 14px;
      color: #333333;
      font-weight: 500;
    }
  }
}

.separator-line {
  height: 12px;
  background: #f5f6f7;
  margin: 0 -24px;
}

.trade-card {
  background: #f9fafb;
  border-radius: 12px;
  padding: 20px;
  margin-top: 12px;

  .detail-row {
    display: flex;
    justify-content: space-between;
    padding: 12px 0;
    border-bottom: 1px solid #f0f0f0;

    &:last-child {
      border-bottom: none;
    }

    &.order-no-row {
      .order-no-wrap {
        display: flex;
        align-items: center;
        gap: 12px;

        .order-no {
          font-family: monospace;
        }

        .copy-btn {
          font-size: 14px;
          color: #4063ff;
          cursor: pointer;
        }
      }
    }

    .detail-label {
      font-size: 14px;
      color: #999999;
    }

    .detail-value {
      font-size: 14px;
      color: #333333;

      &.transaction-info {
        color: #4063ff;
      }
    }
  }
}

.tip-card {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 20px;
  margin-top: 20px;
  background: #e1eaff;
  border-radius: 12px;
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    background: #d0deff;
  }

  .tip-text {
    font-size: 14px;
    color: #4063ff;
  }

  i {
    font-size: 18px;
    color: #4063ff;
  }
}

.bottom-actions {
  display: flex;
  gap: 16px;
  margin-top: 24px;
  padding-top: 24px;
  border-top: 1px solid #f0f0f0;

  .action-btn {
    flex: 1;
    height: 48px;
    border-radius: 8px;
    font-size: 16px;
    font-weight: 600;

    &.cancel-btn {
      border-color: #d9d9d9;
      color: #666666;
    }

    &.pay-btn {
      background: #4063ff;
      border-color: #4063ff;
      color: #ffffff;
    }
  }
}

.pay-modal-content {
  .modal-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 24px;

    .modal-title {
      font-size: 18px;
      font-weight: 600;
      color: #333333;
    }

    .modal-close {
      font-size: 24px;
      color: #999999;
      cursor: pointer;
    }
  }

  .pay-amount-section {
    text-align: center;
    margin-bottom: 24px;

    .pay-label {
      display: block;
      font-size: 14px;
      color: #999999;
      margin-bottom: 8px;
    }

    .pay-amount {
      font-size: 32px;
      font-weight: 700;
      color: #333333;
    }
  }

  .pay-method-section {
    margin-bottom: 20px;

    .section-title {
      display: block;
      font-size: 14px;
      color: #333333;
      margin-bottom: 12px;
    }

    .pay-method-item {
      display: flex;
      align-items: center;
      padding: 12px 16px;
      margin-bottom: 8px;
      background: #f9fafb;
      border-radius: 8px;
      cursor: pointer;
      transition: all 0.2s;

      &.active {
        background: #e1eaff;
        border: 1px solid #4063ff;
      }

      .method-radio {
        width: 20px;
        height: 20px;
        border: 2px solid #d9d9d9;
        border-radius: 50%;
        margin-right: 12px;
        display: flex;
        align-items: center;
        justify-content: center;

        .radio-inner {
          width: 10px;
          height: 10px;
          background: #4063ff;
          border-radius: 50%;
        }
      }

      .method-name {
        flex: 1;
        font-size: 14px;
        color: #333333;
      }

      .method-balance {
        font-size: 12px;
        color: #999999;
      }
    }
  }

  .password-section {
    margin-bottom: 24px;

    .section-title {
      display: block;
      font-size: 14px;
      color: #333333;
      margin-bottom: 12px;
    }

    .password-input {
      width: 100%;
    }
  }

  .modal-footer {
    .submit-btn {
      width: 100%;
      height: 48px;
      line-height: 48px;
      text-align: center;
      background: #4063ff;
      color: #ffffff;
      font-size: 16px;
      font-weight: 600;
      border-radius: 8px;
      cursor: pointer;
      transition: all 0.2s;

      &:hover {
        background: #3050e0;
      }
    }
  }
}

.success-modal-content {
  text-align: center;
  padding: 40px 20px;

  .success-icon {
    width: 80px;
    height: 80px;
    line-height: 80px;
    background: #52c41a;
    color: #ffffff;
    font-size: 36px;
    font-weight: bold;
    border-radius: 50%;
    margin: 0 auto 20px;
  }

  .success-title {
    display: block;
    font-size: 20px;
    font-weight: 600;
    color: #333333;
    margin-bottom: 24px;
  }

  .success-btn {
    display: inline-block;
    padding: 12px 40px;
    background: #4063ff;
    color: #ffffff;
    font-size: 16px;
    font-weight: 600;
    border-radius: 8px;
    cursor: pointer;
    transition: all 0.2s;

    &:hover {
      background: #3050e0;
    }
  }
}
</style>
